import React, { useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, ArrowRight, Shield, CreditCard, Calendar } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { Navigation } from '../../components/Navigation'
import { useAuth } from '../../hooks/useAuth'
import { getUserSubscription } from '../../lib/stripe'
import toast from 'react-hot-toast'

export const CheckoutSuccess: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [subscription, setSubscription] = React.useState<any>(null)
  const [loading, setLoading] = React.useState(true)

  // Get session_id from URL
  const sessionId = searchParams.get('session_id')

  useEffect(() => {
    if (!user) {
      navigate('/auth/login')
      return
    }

    // Load subscription data
    const loadSubscription = async () => {
      try {
        setLoading(true)
        const { subscription: subData } = await getUserSubscription()
        setSubscription(subData)
      } catch (error) {
        console.error('Error loading subscription:', error)
        toast.error('Failed to load subscription details')
      } finally {
        setLoading(false)
      }
    }

    loadSubscription()
  }, [user, navigate])

  const formatDate = (timestamp: number) => {
    if (!timestamp) return 'N/A'
    return new Date(timestamp * 1000).toLocaleDateString()
  }

  return (
    <div className="min-h-screen bg-calming">
      <Navigation variant="dashboard" />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Card className="p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
              Payment Successful!
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              Thank you for your subscription. Your payment has been processed successfully, and your account has been upgraded.
            </p>
            
            {loading ? (
              <div className="animate-pulse bg-slate-100 h-32 rounded-lg mb-8"></div>
            ) : subscription ? (
              <div className="bg-sage-50 rounded-lg p-6 mb-8 text-left">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Subscription Details</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <CreditCard className="w-5 h-5 text-slate-500 mr-3" />
                    <div>
                      <p className="font-medium text-slate-900">
                        {subscription.product?.name || 'Premium'} Plan
                      </p>
                      <p className="text-sm text-slate-500">
                        ${subscription.product?.price || '--'}/month per user
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-slate-500 mr-3" />
                    <div>
                      <p className="font-medium text-slate-900">
                        Next Billing Date
                      </p>
                      <p className="text-sm text-slate-500">
                        {formatDate(subscription.current_period_end)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Shield className="w-5 h-5 text-slate-500 mr-3" />
                    <div>
                      <p className="font-medium text-slate-900">
                        Status
                      </p>
                      <p className="text-sm text-emerald-600 font-medium">
                        Active
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 rounded-lg p-6 mb-8 text-left">
                <h3 className="text-lg font-semibold text-amber-900 mb-2">Processing Your Subscription</h3>
                <p className="text-amber-700">
                  Your payment was successful, but we're still setting up your subscription. 
                  This should only take a moment. Please refresh this page in a few seconds.
                </p>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/dashboard">
                <Button className="bg-sage-600 hover:bg-sage-700 text-white px-8">
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              
              <Link to="/settings?tab=subscription">
                <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8">
                  Manage Subscription
                </Button>
              </Link>
            </div>
          </Card>
          
          <div className="mt-8 text-center text-sm text-slate-500">
            <p>
              If you have any questions about your subscription, please contact our support team at{' '}
              <a href="mailto:support@mentra.com" className="text-sage-600 hover:underline">
                support@mentra.com
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  )
}